import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  message,
  Typography,
  Avatar
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import videoService from '../../services/videoService';
import { useUser } from '../../context/UserContext';

const { Search } = Input;
const { Title, Text } = Typography;

const CategoryList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [videoCategories, setVideoCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} categories`
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    searchText: ''
  });

  // Table columns configuration
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 80,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 100,
      sorter: (a, b) => a.order - b.order
    },
    {
      title: 'ID',
      dataIndex: 'vc_id',
      key: 'vc_id',
      width: 100,
      sorter: (a, b) => a.vc_id - b.vc_id
    },
    {
      title: 'Category Name',
      dataIndex: 'vc_name',
      key: 'vc_name',
      sorter: (a, b) => a.vc_name.localeCompare(b.vc_name)
    },
    {
      title: 'Status',
      dataIndex: 'vc_is_active',
      key: 'vc_is_active',
      width: 120,
      render: (text) => (
        <Tag color={text === 'Y' ? 'green' : 'red'}>
          {text === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'Y' },
        { text: 'Inactive', value: 'N' }
      ],
      onFilter: (value, record) => record.vc_is_active === value
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/categories/edit/${record.vc_id}`)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      )
    }
  ];

  // Fetch video categories data
  const getAllVideoCategory = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching video categories with params:', queryParams);
      const response = await videoService.getAllVideoCategory(queryParams);
      console.log('API Response:', response);
      
      const { data } = response;
      
      if (data && data.status) {
        setVideoCategories(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.data?.length || 0
        }));
      } else {
        console.log('API returned no data or status false:', data);
        setVideoCategories([]);
      }
    } catch (error) {
      console.error('Error fetching video categories:', error);
      message.error('Failed to fetch video categories');
      setVideoCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Update video category order
  const updateVideoCategoryOrder = async (params) => {
    try {
      setSaving(true);
      console.log('Updating video category order:', params);
      
      const response = await videoService.updateVideoCategoryOrder(params);
      console.log('Update Response:', response);
      
      if (response.data && response.data.status) {
        message.success('Category order updated successfully!');
        // Refresh the data
        getAllVideoCategory({
          searchText: filters.searchText,
          perPage: pagination.pageSize,
          currentPage: pagination.current
        });
      } else {
        message.error(response.data.message || 'Failed to update category order');
      }
    } catch (error) {
      console.error('Error updating video category order:', error);
      message.error('Failed to update category order');
    } finally {
      setSaving(false);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getAllVideoCategory({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const { current, pageSize } = newPagination;
    
    if (current !== pagination.current || pageSize !== pagination.pageSize) {
      setPagination(prev => ({
        ...prev,
        current,
        pageSize
      }));
      
      getAllVideoCategory({
        searchText: filters.searchText,
        perPage: pageSize,
        currentPage: current
      });
    }
  };

  // Handle add new category
  const handleAddNew = () => {
    navigate('/categories/addcategory');
  };

  // Load data on component mount
  useEffect(() => {
    getAllVideoCategory();
  }, []);

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Category Management</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Category Management"
      desc="Manage video categories and their order"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        {/* Table Operations */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              style={{ marginRight: '15px' }}
            >
              Add Category
            </Button>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Search
              placeholder="Search categories..."
              style={{ width: 300 }}
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              onSearch={handleSearch}
              allowClear
            />
          </div>
        </div>

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={videoCategories}
          rowKey="vc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 600 }}
          size="middle"
        />
      </Card>
    </PageLayout>
  );
};

export default CategoryList;